/**
 * Policy Routing Engine Hub Page
 *
 * Hub-and-spoke navigation page for the Policy Routing Engine (PRE).
 * Displays summary statistics and links to sub-sections for jurisdictions,
 * calling hours, DNC registries, AI classification, and decision logs.
 */
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, cn } from '@respectly/ui';
import {
  Scale,
  ArrowLeft,
  Globe,
  Clock,
  ShieldBan,
  Brain,
  FileSearch,
  MapPin,
  BookOpen,
  CheckCircle,
  XCircle,
  UserPlus,
  Package,
  type LucideIcon,
} from 'lucide-react';
import {
  SettingsNavigationCard,
  SettingsNavigationGrid,
} from '../../components/settings';
import {
  usePolicyStore,
  useActiveJurisdictionCount,
  useActiveRulesCount,
  useDecisionStats,
} from '../../stores/usePolicyStore';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// Navigation Items
// ============================================

const POLICY_SECTIONS = [
  {
    icon: BookOpen,
    title: 'Documentation',
    description:
      'End-to-end guide to PRE — setup, rules, jurisdictions, decision log, and more',
    to: '/settings/policy/docs',
    iconColor: 'text-violet-600 dark:text-violet-400',
    iconBgColor: 'bg-violet-100 dark:bg-violet-900/40',
  },
  {
    icon: Globe,
    title: 'Jurisdiction Rules',
    description: 'View and configure compliance rules per jurisdiction',
    to: '/settings/policy/jurisdictions',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    iconBgColor: 'bg-cyan-100 dark:bg-cyan-900/40',
  },
  {
    icon: UserPlus,
    title: 'Contact Creation',
    description: 'Default consent state granted when contacts are created',
    to: '/settings/policy/consent-capture',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBgColor: 'bg-emerald-100 dark:bg-emerald-900/40',
  },
  {
    icon: Clock,
    title: 'Calling Hours',
    description: 'Manage permitted sending windows by jurisdiction',
    to: '/settings/policy/hours',
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconBgColor: 'bg-amber-100 dark:bg-amber-900/40',
  },
  {
    icon: ShieldBan,
    title: 'DNC & Registries',
    description: 'Internal DNC and national registry configuration',
    to: '/settings/policy/dnc',
    iconColor: 'text-rose-600 dark:text-rose-400',
    iconBgColor: 'bg-rose-100 dark:bg-rose-900/40',
  },
  {
    icon: Brain,
    title: 'AI Classification',
    description: 'Message purpose classification and review',
    to: '/settings/policy/ai',
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBgColor: 'bg-purple-100 dark:bg-purple-900/40',
  },
  {
    icon: FileSearch,
    title: 'Decision Log',
    description: 'Enforcement audit trail with full trace',
    to: '/settings/policy/decisions',
    iconColor: 'text-slate-600 dark:text-slate-400',
    iconBgColor: 'bg-slate-100 dark:bg-slate-900/40',
  },
  {
    icon: Package,
    title: 'Bundle Export / Import',
    description: 'Backup, restore, and golden-master promote the full rule corpus',
    to: '/settings/policy/bundle',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBgColor: 'bg-blue-100 dark:bg-blue-900/40',
  },
];

// ============================================
// Stat Card Component
// ============================================

function StatCard({
  icon: Icon,
  label,
  value,
  annotation,
  iconColor,
  iconBgColor,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  annotation?: string;
  iconColor: string;
  iconBgColor: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                iconBgColor
              )}
            >
              <Icon className={cn('w-5 h-5', iconColor)} />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                  {value}
                </p>
                {annotation && (
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400 truncate">
                    {annotation}
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {label}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Component
// ============================================

export function PolicyEngine() {
  const loadJurisdictions = usePolicyStore((s) => s.loadJurisdictions);
  const loadRules = usePolicyStore((s) => s.loadRules);
  const loadDecisionLogs = usePolicyStore((s) => s.loadDecisionLogs);

  const activeJurisdictions = useActiveJurisdictionCount();
  const activeRules = useActiveRulesCount();
  const decisionStats = useDecisionStats();

  useEffect(() => {
    loadJurisdictions();
    loadRules();
    loadDecisionLogs();
  }, [loadJurisdictions, loadRules, loadDecisionLogs]);

  const conditionalAnnotation =
    decisionStats.conditional > 0
      ? `+${decisionStats.conditional} conditional`
      : undefined;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900/40 dark:to-indigo-800/40 flex items-center justify-center">
            <Scale className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Policy Routing Engine
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Configure compliance rules, calling hours, DNC registries, and AI
              classification
            </p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Stats Bar */}
        <motion.div variants={itemVariants} className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={MapPin}
              label="Active Jurisdictions"
              value={activeJurisdictions}
              iconColor="text-cyan-600 dark:text-cyan-400"
              iconBgColor="bg-cyan-100 dark:bg-cyan-900/40"
            />
            <StatCard
              icon={BookOpen}
              label="Active Rules"
              value={activeRules}
              iconColor="text-indigo-600 dark:text-indigo-400"
              iconBgColor="bg-indigo-100 dark:bg-indigo-900/40"
            />
            <StatCard
              icon={CheckCircle}
              label="Allowed"
              value={decisionStats.allowed}
              annotation={conditionalAnnotation}
              iconColor="text-emerald-600 dark:text-emerald-400"
              iconBgColor="bg-emerald-100 dark:bg-emerald-900/40"
            />
            <StatCard
              icon={XCircle}
              label="Blocked"
              value={decisionStats.blocked}
              iconColor="text-red-600 dark:text-red-400"
              iconBgColor="bg-red-100 dark:bg-red-900/40"
            />
          </div>
        </motion.div>

        {/* Navigation Cards */}
        <motion.div variants={itemVariants}>
          <SettingsNavigationGrid>
            {POLICY_SECTIONS.map((section, index) => (
              <motion.div
                key={section.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <SettingsNavigationCard
                  icon={section.icon}
                  title={section.title}
                  description={section.description}
                  to={section.to}
                  iconColor={section.iconColor}
                  iconBgColor={section.iconBgColor}
                />
              </motion.div>
            ))}
          </SettingsNavigationGrid>
        </motion.div>
      </div>
    </motion.div>
  );
}
